import { Stack, Typography } from '@mui/material';
import { type NotFoundError, type NotFoundRouteProps } from '@tanstack/react-router';
import { ChevronLeftIcon } from 'lucide-react';
import { type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { hasErrorCause, isAccessControlError, isPublicApiError } from '@/api/errors';
import { ErrorFallbackSupportLink } from '@/components/Error/ErrorFallbackSupportLink';
import { reportError } from '@/helpers/errors/report';
import { cdnStaticPath } from '@/helpers/url';
import { ButtonLink } from '@/router/Link';
import { isLocalDevelopmentEnv, isDevelopmentEnv } from '@/utils/utils';
import {
  ErrorContent,
  ErrorImage,
  ErrorContainer,
  ErrorCodeBox,
  ErrorFallbackActions,
  MonospaceTypography,
} from './styled-components';

type NotFoundActions = { back: ReactNode; logout?: ReactNode };

export type DefaultNotFoundFallbackProps<T = NotFoundRouteProps> = PropsWithChildren<
  T & {
    ActionsComponent?: (actions: NotFoundActions) => ReactNode;
  }
>;

const BackAndLogoutActions = ({ back, logout }: NotFoundActions) => (logout ? [back, logout] : [back]);

export const DefaultNotFoundFallback = (props: DefaultNotFoundFallbackProps) => (
  <ErrorContainer data-testid="not-found-boundary">
    <ErrorImage src={cdnStaticPath('static/images/not-found-illustration.svg')} role="presentation" />
    <DefaultNotFoundContent {...props} />
  </ErrorContainer>
);

export const DefaultNotFoundFallbackBasic = (props: DefaultNotFoundFallbackProps) => (
  <DefaultNotFoundContent {...props} />
);

function DefaultNotFoundContent({
  data,
  routeId,
  children,
  ActionsComponent = BackAndLogoutActions,
}: DefaultNotFoundFallbackProps<NotFoundError>) {
  let error: Error | undefined;
  if (hasErrorCause(data)) {
    error = data.cause;
  }

  useEffect(() => {
    if (error) {
      if (isLocalDevelopmentEnv) {
        console.error('NotFound Error Boundary:', error); // oxlint-disable-line no-console
      }
      // Only report errors if the cause was an actual error, not just a 404 from a missing route or param
      // validation. Deduplication against the centralized QueryClient handler (which captures the same
      // instance) is handled in Sentry's `beforeSend`, which reports each error instance exactly once.
      reportError(error, { domain: 'infra', visibility: 'error-page' });
    }
  }, [error]);

  let heading: string;
  let subtitle: string;
  let body: ReactNode;

  if (isAccessControlError(error)) {
    heading = 'Access Denied';
    subtitle = "You don't have the permissions required to access this resource.";
    body = (
      <ErrorCodeBox>
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          Please contact a user who can manage access on this account.
        </Typography>
      </ErrorCodeBox>
    );
  } else {
    let errorDetails: ReactNode;
    if (isLocalDevelopmentEnv || isDevelopmentEnv) {
      errorDetails = routeId && <MonospaceTypography>Boundary Route Id: {routeId}</MonospaceTypography>;
    } else if (isPublicApiError(error) && error.traceId) {
      errorDetails = <MonospaceTypography>Trace ID: {error.traceId}</MonospaceTypography>;
    }
    heading = 'Lost in vector space?';
    subtitle = 'This resource could not be found or does not exist.';
    body = (
      <ErrorCodeBox>
        {children}
        {errorDetails}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          If you expected to find something here, please{' '}
          <ErrorFallbackSupportLink>contact support</ErrorFallbackSupportLink> and provide the page URL.
        </Typography>
      </ErrorCodeBox>
    );
  }

  return (
    <ErrorContent role="alert">
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              color: 'text.primary',
            }}
          >
            {heading}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              color: 'text.primary',
            }}
          >
            {subtitle}
          </Typography>
        </Stack>
        {body}
        <ErrorFallbackActions sx={{ containerType: 'inline-size' }}>
          <ActionsComponent
            back={
              <ButtonLink key="back" to="/" variant="text" size="small" startIcon={<ChevronLeftIcon size={16} />}>
                Back to Known Space
              </ButtonLink>
            }
          />
        </ErrorFallbackActions>
      </Stack>
    </ErrorContent>
  );
}
